import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  Box,
  Typography,
  IconButton,
  InputLabel,
  SelectProps,
  InputAdornment,
  TextField as MuiTextField,
} from '@mui/material';

import { Icon } from '../icons';

import { InputProps, InputVariantClassnameMap } from './types';

const variantClassname: InputVariantClassnameMap = {
  outlined: '',
};

export const Input = ({
  label,
  error,
  value,
  shrink,
  pattern,
  onChange,
  slotProps,
  className,
  multiline,
  expandable,
  placeholder,
  type = 'text',
  variant = 'filled',
  labelOutside = false,
  ...props
}: InputProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [focused, setFocused] = useState(false);
  const isPassword = type === 'password';

  const adornment = isPassword ? (
    <InputAdornment position="end" sx={{ mr: 1 }}>
      <IconButton
        edge="end"
        size="small"
        onClick={() => setShowPassword((prev) => !prev)}
        aria-label={showPassword ? 'Hide password' : 'Show password'}
      >
        {showPassword ? <Icon.EyeOff /> : <Icon.Eye />}
      </IconButton>
    </InputAdornment>
  ) : null;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (pattern) {
      const regex = new RegExp(pattern, 'g');
      const value = event.target.value.replace(regex, '');
      event.target.value = value;
    }
    onChange?.(event);
  };

  const inputSlotProps: InputProps['slotProps'] = {
    inputLabel: { shrink },
    ...slotProps,
    input: {
      endAdornment: adornment,
      className: twMerge(
        'bg-white rounded-xl',
        labelOutside || !label ? 'withoutLabel' : '',
        className
      ),
      ...slotProps?.input,
    },
    ...(slotProps?.select && {
      select: {
        ...slotProps.select,
        displayEmpty: true,
        IconComponent: Icon.ChevronDown,
        renderValue: (selected) => {
          if (typeof selected === 'string') {
            if (selected.length === 0) {
              return (
                <Typography
                  component="span"
                  sx={{
                    color: 'text.disabled',
                  }}
                >
                  {placeholder}
                </Typography>
              );
            }
            return selected;
          }
          if (Array.isArray(selected)) {
            if (selected.length === 0) {
              return (
                <Typography
                  component="span"
                  sx={{
                    color: 'text.disabled',
                  }}
                >
                  {placeholder}
                </Typography>
              );
            }
            return selected.join(', ');
          }
          return selected as string;
        },
      },
    }),
  };

  const isMultipleSelect = (inputSlotProps?.select as SelectProps)?.multiple;
  const isSelect = !!inputSlotProps?.select;
  const normalizedValue = isMultipleSelect
    ? !value
      ? []
      : value
    : isSelect && value === undefined
      ? ''
      : value;

  return (
    <Box
      className={twMerge(
        expandable && 'relative',
        expandable && focused && 'z-10',
        expandable && (label ? 'min-h-13' : 'min-h-10')
      )}
    >
      {labelOutside && label && <InputLabel error={error}>{label}</InputLabel>}
      <MuiTextField
        error={error}
        variant={variant}
        multiline={multiline}
        onChange={handleChange}
        value={normalizedValue}
        placeholder={placeholder}
        slotProps={inputSlotProps}
        label={labelOutside ? undefined : label}
        type={isPassword && showPassword ? 'text' : type}
        maxRows={multiline && expandable ? (focused ? 10 : 1) : undefined}
        className={twMerge(
          variantClassname[variant],
          expandable && focused && 'absolute inset-x-0 top-0',
          className
        )}
        {...props}
        {...(multiline &&
          expandable && {
            onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
              setFocused(false);
              props?.onBlur?.(e);
            },
            onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
              setFocused(true);
              props?.onFocus?.(e);
            },
          })}
      />
    </Box>
  );
};

export type { InputProps };
